import config from '../../../../common/utils/constants/config';
import { mapStyleTypes } from '../../../utils/constants/map-styles';
import {
  removeLayersWhenMapReady,
  createGetLayerIdWithPostfix,
  getLayersIdUniquePostfix,
} from '../../../utils/helpers/interactiveLayerStyleUtils';

export const tileset = config.ROAD_TILESET;
export const sourceId = config.ROAD_SOURCE_ID;
export const sourceLayer = config.ROAD_SOURCE_LAYER;

const getLayerId = createGetLayerIdWithPostfix('default_road');
const getHoveredLayerId = createGetLayerIdWithPostfix('hovered_road');
const getSelectedLayerId = createGetLayerIdWithPostfix('selected_road');

const filter = [
  '!',
  [
    'in',
    [
      'concat',
      ['to-string', ['get', 'class']],
      ':',
      ['to-string', ['get', 'type']],
    ],
    [
      'literal',
      [
        'golf:hole',
        'ferry:auto',
        'path:steps',
        'path:bridleway',
        'path:crossing',
        'aerialway:drag_lift',
        'aerialway:cable_car',
        'aerialway:gondola',
        'aerialway:mixed_lift',
        'aerialway:chair_lift',
        'aerialway:magic_carpet',
        'aerialway:zip_line',
        'aerialway:',
        'pedestrian:platform',
        'service:emergency_access',
        'track:piste',
        'rail:major_rail',
        'rail:service_rail',
        'subway:major_rail',
        'subway:service_rail',
        'abandoned:service_rail',
        'path:sidewalk',
        'path:footway',
        'path:path',
        'path:cycleway',
        'pedestrian:pedestrian',
        'ferry:ferry',
        'ferry:ferry_auto',
      ],
    ],
  ],
];

const getSatelliteBasedStyle = (layersIdPostfix: string) => {
  const layerId = getLayerId(layersIdPostfix);
  const hoveredLayerId = getHoveredLayerId(layersIdPostfix);
  const selectedLayerId = getSelectedLayerId(layersIdPostfix);

  return {
    add: (map) => {
      const style = map.getStyle();

      if (!style.sources[sourceId as string]) {
        map.addSource(sourceId, {
          type: 'vector',
          url: tileset,
        });
      }

      if (!style.layers.some(({ id }) => id === layerId)) {
        map.addLayer({
          id: layerId,
          type: 'line',
          source: sourceId,
          'source-layer': sourceLayer,
          filter,
          paint: {
            'line-color': '#0028fa',
            'line-width': 5,
            'line-opacity': [
              'case',
              ['boolean', ['feature-state', 'hidden'], false],
              0,
              ['boolean', ['feature-state', 'hover'], false],
              0,
              ['boolean', ['feature-state', 'selected'], false],
              0,
              0.4,
            ],
          },
        });
      }

      if (!style.layers.some(({ id }) => id === hoveredLayerId)) {
        map.addLayer({
          id: hoveredLayerId,
          type: 'line',
          source: sourceId,
          'source-layer': sourceLayer,
          filter,
          paint: {
            'line-color': '#fa0000',
            'line-width': 5,
            'line-opacity': [
              'case',
              ['boolean', ['feature-state', 'hidden'], false],
              0,
              ['boolean', ['feature-state', 'selected'], false],
              0,
              ['boolean', ['feature-state', 'hover'], false],
              0.4,
              0,
            ],
          },
        });
      }

      if (!style.layers.some(({ id }) => id === selectedLayerId)) {
        map.addLayer({
          id: selectedLayerId,
          type: 'line',
          source: sourceId,
          'source-layer': sourceLayer,
          filter,
          paint: {
            'line-color': '#fa0000',
            'line-width': 5,
            'line-opacity': [
              'case',
              ['boolean', ['feature-state', 'hidden'], false],
              0,
              ['boolean', ['feature-state', 'hover'], false],
              0,
              ['boolean', ['feature-state', 'selected'], false],
              0.7,
              0,
            ],
          },
        });
      }
    },
    remove: (map) =>
      removeLayersWhenMapReady(map, [layerId, hoveredLayerId, selectedLayerId]),
  };
};

const getStreetsBasedStyle = (layersIdPostfix: string) => {
  const layerId = getLayerId(layersIdPostfix);
  const hoveredLayerId = getHoveredLayerId(layersIdPostfix);
  const selectedLayerId = getSelectedLayerId(layersIdPostfix);

  return {
    add: (map) => {
      const style = map.getStyle();

      if (!style.sources[sourceId as string]) {
        map.addSource(sourceId, {
          type: 'vector',
          url: tileset,
        });
      }

      if (!style.layers.some(({ id }) => id === layerId)) {
        map.addLayer({
          id: layerId,
          type: 'line',
          source: sourceId,
          'source-layer': sourceLayer,
          filter,
          paint: {
            'line-color': '#0028fa',
            'line-width': 5,
            'line-opacity': [
              'case',
              ['boolean', ['feature-state', 'hidden'], false],
              0,
              ['boolean', ['feature-state', 'hover'], false],
              0,
              ['boolean', ['feature-state', 'selected'], false],
              0,
              0.4,
            ],
          },
        });
      }

      if (!style.layers.some(({ id }) => id === hoveredLayerId)) {
        map.addLayer({
          id: hoveredLayerId,
          type: 'line',
          source: sourceId,
          'source-layer': sourceLayer,
          filter,
          paint: {
            'line-color': '#fa0000',
            'line-width': 5,
            'line-opacity': [
              'case',
              ['boolean', ['feature-state', 'hidden'], false],
              0,
              ['boolean', ['feature-state', 'selected'], false],
              0,
              ['boolean', ['feature-state', 'hover'], false],
              0.4,
              0,
            ],
          },
        });
      }

      if (!style.layers.some(({ id }) => id === selectedLayerId)) {
        map.addLayer({
          id: selectedLayerId,
          type: 'line',
          source: sourceId,
          'source-layer': sourceLayer,
          filter,
          paint: {
            'line-color': '#fa0000',
            'line-width': 5,
            'line-opacity': [
              'case',
              ['boolean', ['feature-state', 'hidden'], false],
              0,
              ['boolean', ['feature-state', 'hover'], false],
              0,
              ['boolean', ['feature-state', 'selected'], false],
              0.7,
              0,
            ],
          },
        });
      }
    },
    remove: (map) =>
      removeLayersWhenMapReady(map, [layerId, hoveredLayerId, selectedLayerId]),
  };
};

export const getLayerStyleData = (mapStyleId?: string) => {
  const layersIdPostfix = getLayersIdUniquePostfix(mapStyleId);

  let style: unknown = null;
  switch (mapStyleId) {
    case mapStyleTypes.streets:
      style = getStreetsBasedStyle(layersIdPostfix);
      break;
    case mapStyleTypes.satellite:
    case mapStyleTypes.hybrid:
      style = getSatelliteBasedStyle(layersIdPostfix);
      break;
  }

  return {
    style,
    layerId: getLayerId(layersIdPostfix),
    hoveredLayerId: getHoveredLayerId(layersIdPostfix),
    selectedLayerId: getSelectedLayerId(layersIdPostfix),
  };
};
